import { Box, GridLegacy as Grid, Typography, TypographyVariant, Chip, ChipProps } from '@mui/material'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { ReactNode } from 'react'
import { tableGridHeaderStyles, tableGridStyles } from './styles'

type HeaderProps = {
    icon?: React.ReactElement
    title: ReactNode
    titleVariant?: TypographyVariant
    info?: ReactNode
    chip?: ChipProps
    buttons?: ReactNode | ReactNode[]
    customHeader?: never
}

type CustomHeaderProps = {
    icon?: never
    title?: never
    titleVariant?: never
    info?: never
    chip?: never
    buttons?: never
    customHeader: ReactNode
}

type TableGridHeaderProps = HeaderProps | CustomHeaderProps

export const TableGridHeader = ({
    icon,
    title,
    titleVariant = 'h3',
    info,
    chip,
    buttons,
    customHeader,
}: TableGridHeaderProps): React.ReactElement => {
    if (!title) {
        return (
            <Grid
                container
                item
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={tableGridHeaderStyles.header}
            >
                {customHeader}
            </Grid>
        )
    }
    return (
        <Grid container item direction="row" alignItems="center" justifyContent="space-between" sx={tableGridHeaderStyles.header}>
            <Typography variant={titleVariant} sx={tableGridHeaderStyles.title}>
                {title}
                {icon}
            </Typography>
            <Box>
                {info && <Box sx={tableGridHeaderStyles.info}>{info}</Box>}
                {chip && <Chip color={chip.color} label={chip.label} />}
                {Array.isArray(buttons) ? (
                    buttons?.map((b, i) => {
                        return (
                            <Box component="span" key={i} sx={tableGridHeaderStyles.button}>
                                {b}
                            </Box>
                        )
                    })
                ) : (
                    <span>{buttons}</span>
                )}
            </Box>
        </Grid>
    )
}

export enum SpacingVariants {
    default,
    compact,
    noPadding,
}

type TableGridProps = TableGridHeaderProps & {
    children: ReactNode
    spacingVariant?: SpacingVariants
}

/**
 * @deprecated Use C1TableGrid component instead
 */
export const TableGrid = ({
    icon,
    title,
    titleVariant = 'h4',
    info,
    chip,
    buttons,
    customHeader,
    children,
    spacingVariant = SpacingVariants.default,
}: TableGridProps): React.ReactElement => {
    const getContainerStyle = (): typeof tableGridStyles.bodyDefault => {
        switch (spacingVariant) {
            case SpacingVariants.compact:
                return tableGridStyles.bodyCompact
            case SpacingVariants.noPadding:
                return tableGridStyles.bodyNoPadding
            case SpacingVariants.default:
            default:
                return tableGridStyles.bodyDefault
        }
    }

    let header = <TableGridHeader customHeader={customHeader} />
    if (title) {
        header = (
            <TableGridHeader
                icon={icon}
                title={title}
                titleVariant={titleVariant}
                info={info}
                chip={chip}
                buttons={buttons}
            />
        )
    }

    return (
        <Grid
            container
            direction="column"
            alignItems="flex-start"
            alignContent="flex-start"
            sx={tableGridStyles.tableGrid}
        >
            {header}
            <Grid container item sx={sxc(getContainerStyle(), tableGridStyles.body)}>
                {children}
            </Grid>
        </Grid>
    )
}
